/**
 * Token-based markdown renderer with inline-SVG splitting.
 *
 * Streaming AI deltas can contain raw <svg>…</svg> blocks. marked.lexer
 * only recognises HTML once it's well-formed, so partial SVG arrives as
 * paragraph text and never renders. We pre-scan the string for <svg>
 * boundaries (port of mobile message_bubble.dart _buildContentSegments)
 * and split into three kinds of segments:
 *   - 'markdown'    : prose between SVG blocks → marked.lexer pipeline
 *   - 'svg'         : complete <svg>…</svg>    → InlineSvg
 *   - 'svg-partial' : trailing <svg… open tag  → ProgressiveSvg
 *
 * Code fences whose body is a complete SVG (```svg\n<svg…</svg>\n```)
 * are unwrapped first so the fence markers don't leak past the scan
 * (mirrors mobile's identical pre-pass).
 */
import React, { useMemo } from 'react';
import { marked } from 'marked';
import MarkdownTokens from './MarkdownTokens';
import InlineSvg from './InlineSvg';
import ProgressiveSvg from './ProgressiveSvg';

marked.use({ breaks: true, gfm: true });

const SVG_FENCE_RE = /```(?:svg|html|xml)?\s*\n(\s*<svg[\s\S]*?<\/svg>\s*)\n```/gm;

const splitSegments = (text) => {
  if (!text) return [];
  const stripped = text.replace(SVG_FENCE_RE, (_, body) => body || '');
  const segments = [];
  let i = 0;
  while (i < stripped.length) {
    const openIdx = stripped.indexOf('<svg', i);
    if (openIdx < 0) {
      const tail = stripped.slice(i);
      if (tail.trim()) segments.push({ kind: 'markdown', text: tail });
      break;
    }
    if (openIdx > i) {
      const pre = stripped.slice(i, openIdx);
      if (pre.trim()) segments.push({ kind: 'markdown', text: pre });
    }
    const closeIdx = stripped.indexOf('</svg>', openIdx);
    if (closeIdx < 0) {
      segments.push({ kind: 'svg-partial', text: stripped.slice(openIdx) });
      break;
    }
    const end = closeIdx + '</svg>'.length;
    segments.push({ kind: 'svg', text: stripped.slice(openIdx, end) });
    i = end;
  }
  return segments;
};

const lexerSafe = (text) => {
  try {
    return marked.lexer(text);
  } catch (e) {
    console.error('Markdown lexer error:', e);
    return [{ type: 'paragraph', tokens: [{ type: 'text', text }] }];
  }
};

const MarkdownRenderer = ({ text, className = '' }) => {
  const segments = useMemo(() => splitSegments(text), [text]);

  return (
    <div className={`prose dark:prose-invert prose-p:my-0 prose-headings:my-1 prose-pre:my-0 prose-img:my-1 prose-hr:my-4 prose-blockquote:my-0 max-w-none break-words w-full min-w-0 ${className}`}>
      {segments.map((seg, idx) => {
        if (seg.kind === 'svg') return <InlineSvg key={idx} svg={seg.text} />;
        if (seg.kind === 'svg-partial') return <ProgressiveSvg key={idx} partial={seg.text} />;
        return <MarkdownTokens key={idx} tokens={lexerSafe(seg.text)} />;
      })}
    </div>
  );
};

export default React.memo(MarkdownRenderer);
